import * as React from "react";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { HotelsProps, Galleries } from "./typings";
import { inject, observer } from "mobx-react";
import { HotelCard } from "./components/HotelCard";
import { SeeAllContentLink } from "./components/SeeAllContentLink";
import { Hotel, HotelsFlexModuleResult } from "typings/microserviceModels/hotels-flex-module";
import { UitkCardTextSegment } from "node_modules/uitk-react-cards";
import { CardContentReviewsPriceProposition } from "components/flexComponents/Hotels/components/CardContentReviewsPriceProposition";
import { SeeAllButton } from "components/flexComponents/Hotels/components/SeeAllButton";
import { LegalText } from "components/flexComponents/Hotels/components/LegalText";
import { Viewport, ViewSmall, ViewMedium } from "bernie-view";
import { ExtendedContextStore } from "src/typings/flexFramework/FlexDefinitions";
import { isVariantEnabled, Experiment } from "src/components/utility/experiment";
import { HotelInterruption } from "./components/HotelInterruption/HotelInterruption";
import { SchemaOrgScript } from "components/utility/SchemaOrg/SchemaOrgScript";

/**
 * This component is the main overview of a property and gives basic information
 * to users in the hopes of getting them to click and purchase nights in it.
 * Handles mobile and desktop views.
 * TODO: Clean up {@see Experiment.Blossom_Hotel_Image_Carousel} when completed
 */
const HotelsComponent: React.FC<HotelsProps> = inject("hotels")((props: HotelsProps) => {
  const { flexComponent, context } = props;
  const keyHelper = new ItemKeyHelper("hotels");
  const model = flexComponent.model;
  const locationType =
    context &&
    context.searchContext &&
    context.searchContext.location &&
    context.searchContext.location.type;

  const isSeeAllLinkDesktopExperimentEnabled = isVariantEnabled(
    props.context,
    Experiment.BEX_See_All_Properties_Link_Desktop
  );

  const isAffinityFiltersSeeMoreLinkExperimentEnabled = isVariantEnabled(
    props.context,
    Experiment.BEX_HTG_Affinity_Filters_See_More_Link
  );

  const affinityDescription =
    context &&
    context.searchContext &&
    context.searchContext.affinity &&
    context.searchContext.affinity.description;

  const hotels: Hotel[] = model.hotels;
  const [galleries, setGalleries] = React.useState<Galleries>({});
  const hotelsOnMap = typeof model.showMarkersOnMap === "undefined" || model.showMarkersOnMap;

  React.useEffect(() => {
    if (props.hotels && hotels && hotels.length > 0) {
      if (hotelsOnMap) {
        props.hotels.fetchMapHotelsById(hotels, props.context);
      }

      if (isVariantEnabled(context, Experiment.Blossom_Hotel_Image_Carousel)) {
        props.hotels!.fetchGallery(hotels, context).then(newGalleries => {
          setGalleries(newGalleries);
        });
      }
    }
  }, []);

  if (!flexComponent || !model || !hotels || hotels.length === 0 || !props.hotels) {
    return null;
  }

  return (
    <div
      className={`Hotels all-cell-1-1 uitk-cell all-y-padding-three ${
        model.showRedPrices ? "hotelPricingApac" : ""
      }`}
      id={flexComponent.id}
      data-fm={flexComponent.fmId}
      data-fm-title-id={flexComponent.fmTitleId}
    >
      <h2 className="uitk-type-heading-600">{model.title}</h2>
      <SchemaOrgScript structuredData={flexComponent.model.structuredData} />
      <Viewport>
        <ViewMedium>
          <>
            {isSeeAllLinkDesktopExperimentEnabled && (
              <SeeAllContentLink
                model={model}
                isAffinityFiltersSeeMoreLinkExperimentEnabled={
                  isAffinityFiltersSeeMoreLinkExperimentEnabled
                }
                affinityDescription={affinityDescription}
              />
            )}
            {hotels.map((hotel, index) => (
              <Result
                key={keyHelper.next()}
                hotel={hotel}
                index={index}
                isMobile={false}
                hotelsProps={props}
                cardContent={renderCardContent}
                galleries={galleries}
              />
            ))}
          </>
        </ViewMedium>
        <ViewSmall>
          <>
            <SeeAllContentLink
              model={model}
              isAffinityFiltersSeeMoreLinkExperimentEnabled={
                isAffinityFiltersSeeMoreLinkExperimentEnabled
              }
              affinityDescription={affinityDescription}
            />
            {hotels.map((hotel, index) => (
              <Result
                key={keyHelper.next()}
                hotel={hotel}
                index={index}
                isMobile={true}
                hotelsProps={props}
                cardContent={renderCardContent}
                galleries={galleries}
              />
            ))}
          </>
        </ViewSmall>
      </Viewport>
      <SeeAllButton
        showMoreStrategy={model.showMoreStrategy}
        tokens={model.tokens}
        seeAllLink={model.seeAllLink}
        seeAllType={model.seeAllType}
        moduleName={model.name}
        locationName={model.locationName}
        locationType={locationType}
        isAffinityFiltersSeeMoreLinkExperimentEnabled={
          isAffinityFiltersSeeMoreLinkExperimentEnabled
        }
        affinityDescription={affinityDescription}
      />
      <LegalText showMoreStrategy={model.showMoreStrategy} />
    </div>
  );
});

export const Hotels = observer(HotelsComponent);
export default Hotels;

interface ResultProps {
  hotel: Hotel;
  index: number;
  isMobile: boolean;
  hotelsProps: HotelsProps;
  cardContent: CardContentFunc;
  galleries: Galleries;
}

const Result: React.FC<ResultProps> = ({
  hotel,
  index,
  isMobile,
  hotelsProps: {
    context,
    flexComponent: { model },
    l10n
  },
  cardContent,
  galleries
}) => {
  const hotelCard = (
    <HotelCard
      hotel={hotel}
      priceStrategy={model.priceStrategy}
      index={index + 1}
      callToActionHotelName={model.callToActionHotelName}
      context={context}
      l10n={l10n}
      gallery={galleries[hotel.hotelId]}
      CardContent={cardContent(hotel, isMobile, model, context)}
      className="all-t-margin-three"
      isMobile={isMobile}
    />
  );

  if (model.alternativeHotelsOptions && model.alternativeHotelsOptions.position === index) {
    return (
      <>
        <HotelInterruption {...model.alternativeHotelsOptions} isMobile={isMobile} l10n={l10n} />
        {hotelCard}
      </>
    );
  }

  return hotelCard;
};

type CardContentFunc = (
  hotel: Hotel,
  isMobile: boolean,
  model: HotelsFlexModuleResult,
  context: ExtendedContextStore
) => () => JSX.Element;

const renderCardContent: CardContentFunc = (hotel, isMobile, model, context) => () => (
  <UitkCardTextSegment className="first">
    <CardContentReviewsPriceProposition
      hotel={hotel}
      locale={context.locale}
      context={context}
      isMobile={isMobile}
      showGetPriceIfNoPrice={model.showGetPriceIfNoPrice}
    />
  </UitkCardTextSegment>
);
